import React, { useState } from 'react';
import { Journey, JourneyStep, CompletionMethod, JourneySetup, SkipType } from '../../types/journey';
import { StepTimeline } from './StepTimeline';
import { StepDetailView } from './StepDetailView';
// import { ProgressBar } from './ProgressBar';
import { PersonaBadge } from './PersonaBadge';
import { JourneySummary } from './JourneySummary';
import { JourneyResetDialog } from './JourneyResetDialog';
import { StepSkipDialog } from './StepSkipDialog';
import { useLanguage } from '../../context/LanguageContext';
import './WorkflowDisplay.css';

interface WorkflowDisplayProps {
  journey: Journey;
  steps: JourneyStep[];
  selectedIdea?: import('../../types/ai').BusinessIdea | null;
  validationSurvey?: { surveyId: string; ideaTitle: string; createdAt: string } | null;
  preReleaseProject?: { projectId: string; projectTitle: string; createdAt: string } | null;
  onCreateValidationSurveyForIdea?: (idea: import('../../types/ai').BusinessIdea, language?: string) => Promise<void> | void;
  onStepAction: (stepId: string, actionType: string, templateId?: string, selectedIdea?: import('../../types/ai').BusinessIdea, language?: string) => void;
  onCompleteStep: (stepId: string, method: CompletionMethod, reflectionNotes?: string) => void;
  onSkipStep: (stepId: string, justification: string, skipType: SkipType) => void;
  onResetJourney: (newSetup?: JourneySetup, reason?: string, preserveCustomizations?: boolean) => void;
  onJourneyUpdated: (journey: Journey, steps: JourneyStep[]) => void;
}

export const WorkflowDisplay: React.FC<WorkflowDisplayProps> = ({
  journey,
  steps,
  selectedIdea,
  validationSurvey,
  preReleaseProject,
  onCreateValidationSurveyForIdea,
  onStepAction,
  onCompleteStep,
  onSkipStep,
  onResetJourney,
  onJourneyUpdated
}) => {
  const { t } = useLanguage();
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [showSkipDialog, setShowSkipDialog] = useState(false);
  const [stepToSkip, setStepToSkip] = useState<JourneyStep | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedStepIndex, setSelectedStepIndex] = useState<number | null>(null);
  
  const currentStep = steps.find(step => step.status === 'current');
  const completedSteps = steps.filter(step => step.status === 'completed');
  const totalSteps = steps.length;
  
  // Reset selectedStepIndex when the selected step becomes completed and there's a new current step
  React.useEffect(() => {
    if (selectedStepIndex !== null) {
      const selectedStep = steps[selectedStepIndex];
      // If the selected step was just completed and there's a new current step, reset to show current
      if (selectedStep?.status === 'completed' && currentStep && currentStep.stepIndex !== selectedStepIndex) {
        setSelectedStepIndex(null);
      }
    }
  }, [steps, selectedStepIndex, currentStep]);
  
  // Determine which step to display: selected step or current step
  const displayStep = selectedStepIndex !== null ? steps[selectedStepIndex] : currentStep;
  const isViewingCompletedStep = selectedStepIndex !== null && steps[selectedStepIndex]?.status === 'completed';
  // const progressPercentage = (completedSteps.length / totalSteps) * 100;

  const handleSkipStepClick = (step: JourneyStep) => {
    setStepToSkip(step);
    setShowSkipDialog(true);
  };

  const handleSkipStep = async (justification: string, skipType: SkipType) => {
    if (!stepToSkip) return;
    
    try {
      setIsLoading(true);
      await onSkipStep(stepToSkip.id, justification, skipType);
      setShowSkipDialog(false);
      setStepToSkip(null);
    } catch (error) {
      console.error('Error skipping step:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetJourney = async (newSetup?: JourneySetup, reason?: string, preserveCustomizations?: boolean) => {
    try {
      setIsLoading(true);
      await onResetJourney(newSetup, reason, preserveCustomizations);
      setShowResetDialog(false);
    } catch (error) {
      console.error('Error resetting journey:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="workflow-display">
      <div className="workflow-header">
        <div className="workflow-title">
          <h1>{t('journey.yourStartupJourney')}</h1>
          <PersonaBadge persona={journey.persona} />
        </div>
      </div>

      <div className="workflow-content">
        <div className="workflow-main">
          <StepTimeline 
            steps={steps}
            currentStepIndex={journey.currentStepIndex}
            onStepSelect={(stepIndex: number) => {
              // Allow selecting current or completed steps
              const step = steps[stepIndex];
              if (step && (step.status === 'current' || step.status === 'completed')) {
                setSelectedStepIndex(stepIndex);
              }
            }}
          />
          
          {/* Show message when viewing completed step */}
          {isViewingCompletedStep && (
            <div className="viewing-completed-step-banner">
              <div className="banner-content">
                <i className="bi bi-info-circle-fill"></i>
                <div>
                  <strong>{t('journey.viewingCompletedStep')}</strong>
                  <p>{t('journey.viewingCompletedStepDesc')}</p>
                </div>
              </div>
              <button 
                onClick={() => setSelectedStepIndex(null)} 
                className="btn btn-sm btn-primary"
              >
                <i className="bi bi-arrow-right"></i>
                {t('journey.returnToCurrentStep')}
              </button>
            </div>
          )}
          
          {displayStep && (
            <StepDetailView
              step={displayStep}
              selectedIdea={selectedIdea}
              validationSurvey={validationSurvey}
              preReleaseProject={preReleaseProject}
              onCreateValidationSurveyForIdea={onCreateValidationSurveyForIdea}
              onAction={onStepAction}
              onComplete={onCompleteStep}
              onSkip={() => handleSkipStepClick(displayStep)}
              isReviewMode={isViewingCompletedStep}
              journey={journey}
            />
          )}
        </div>

        <div className="workflow-sidebar">
          <JourneySummary 
            journey={journey}
            steps={steps}
            completedSteps={completedSteps.length}
            totalSteps={totalSteps}
            onReset={() => setShowResetDialog(true)}
          />
        </div>
      </div>

      {/* Dialogs */}
      <JourneyResetDialog
        journey={journey}
        isOpen={showResetDialog}
        onClose={() => setShowResetDialog(false)}
        onReset={handleResetJourney}
        isLoading={isLoading}
      />

      {stepToSkip && (
        <StepSkipDialog
          step={stepToSkip}
          isOpen={showSkipDialog}
          onClose={() => {
            setShowSkipDialog(false);
            setStepToSkip(null);
          }}
          onSkip={handleSkipStep}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};
